import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getProfile } from './store/slices/userProfileSlice';
import { getCurrentUser, logout } from './store/slices/authSlice';
import { getToken } from './services/api';
import { OfflineIndicator } from './components/ui/OfflineIndicator';
import { Toaster } from 'react-hot-toast';

import type { AppDispatch, RootState } from './lib/store';
import { MarketingLayout } from './marketing/MarketingLayout';
import { marketingRoutes } from './marketing/marketingRoutes';
import { GetStartedPage } from './marketing/pages/GetStartedPage';
import { MealPlansPage } from './marketing/pages/MealPlansPage';

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-cream-50 dark:bg-dark-surface">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-gold-200 border-t-gold-600 rounded-full animate-spin mx-auto mb-4" />
      <p className="text-neutral-700 dark:text-neutral-300">Loading...</p>
    </div>
  </div>
);

// Lazy loaded pages - Auth
const LoginPage = React.lazy(() => import('./pages/auth/LoginPage').then(m => ({ default: m.LoginPage })));
const SignupPage = React.lazy(() => import('./pages/auth/SignupPage').then(m => ({ default: m.SignupPage })));
const OTPPage = React.lazy(() => import('./pages/auth/OTPPage').then(m => ({ default: m.OTPPage })));
const ForgotPasswordPage = React.lazy(() => import('./pages/auth/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = React.lazy(() => import('./pages/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));

// Core pages
const OnboardingPage = React.lazy(() => import('./pages/onboarding/OnboardingPage').then(m => ({ default: m.OnboardingPage })));
const DashboardPage = React.lazy(() => import('./pages/dashboard/DashboardPage').then(m => ({ default: m.DashboardPage })));
const ProfilePage = React.lazy(() => import('./pages/profile/ProfilePage').then(m => ({ default: m.ProfilePage })));
const MyNutritionPage = React.lazy(() => import('./pages/profile/MyNutritionPage').then(m => ({ default: m.MyNutritionPage })));
const SettingsPage = React.lazy(() => import('./pages/settings/SettingsPage').then(m => ({ default: m.SettingsPage })));
const ContactPage = React.lazy(() => import('./pages/settings/ContactPage').then(m => ({ default: m.ContactPage })));

// Meal pages
const MealPhotoCapturePage = React.lazy(() => import('./pages/meals/MealPhotoCapturePage').then(m => ({ default: m.MealPhotoCapturePage })));
const AddMealPage = React.lazy(() => import('./pages/meals/AddMealPage').then(m => ({ default: m.AddMealPage })));

// Recipe pages
const RecipesPage = React.lazy(() => import('./pages/recipes/RecipesPage').then(m => ({ default: m.RecipesPage })));
const RecipeDetailPage = React.lazy(() => import('./pages/recipes/RecipeDetailPage').then(m => ({ default: m.RecipeDetailPage })));

// Health pages
const WeightTrackingPage = React.lazy(() => import('./pages/health/WeightTrackingPage').then(m => ({ default: m.WeightTrackingPage })));
const WaterTrackingPage = React.lazy(() => import('./pages/health/WaterTrackingPage').then(m => ({ default: m.WaterTrackingPage })));
const ProgressPage = React.lazy(() => import('./pages/health/ProgressPage').then(m => ({ default: m.ProgressPage })));
const HealthInsightsPage = React.lazy(() => import('./pages/health/HealthInsightsPage').then(m => ({ default: m.HealthInsightsPage })));
const ProgressPhotosPage = React.lazy(() => import('./pages/health/ProgressPhotosPage').then(m => ({ default: m.ProgressPhotosPage })));
const BodyCompositionPage = React.lazy(() => import('./pages/health/BodyCompositionPage').then(m => ({ default: m.BodyCompositionPage })));
const MealFeedbackPage = React.lazy(() => import('./pages/health/MealFeedbackPage').then(m => ({ default: m.MealFeedbackPage })));

// Other pages
const AssistantPage = React.lazy(() => import('./pages/assistant/AssistantPage').then(m => ({ default: m.AssistantPage })));
const FoodSearchPage = React.lazy(() => import('./pages/food-search/FoodSearchPage').then(m => ({ default: m.FoodSearchPage })));

// Exercise pages
const ExercisesList = React.lazy(() => import('./pages/exercises/ExercisesList'));
const ExerciseDetail = React.lazy(() => import('./pages/exercises/ExerciseDetail'));

// Workout pages
const WorkoutPlansList = React.lazy(() => import('./pages/workouts/WorkoutPlansList'));
const WorkoutBuilder = React.lazy(() => import('./pages/workouts/WorkoutBuilder'));
const WorkoutTracker = React.lazy(() => import('./pages/workouts/WorkoutTracker'));
const WorkoutAnalytics = React.lazy(() => import('./pages/workouts/WorkoutAnalytics'));

// Diet Plan pages
const DietPlansList = React.lazy(() => import('./pages/diet-plans/DietPlansList'));
const MealPlanGenerator = React.lazy(() => import('./pages/diet-plans/MealPlanGenerator'));
const ShoppingList = React.lazy(() => import('./pages/diet-plans/ShoppingList'));
const DietTemplatesPage = React.lazy(() => import('./pages/diet-templates/DietTemplatesPage'));

// Subscription pages
const SubscriptionTiersPage = React.lazy(() => import('./pages/subscription/SubscriptionTiersPage').then(m => ({ default: m.SubscriptionTiersPage })));
const CheckoutPage = React.lazy(() => import('./pages/subscription/CheckoutPage').then(m => ({ default: m.CheckoutPage })));
const SubscriptionSuccessPage = React.lazy(() => import('./pages/subscription/SubscriptionSuccessPage').then(m => ({ default: m.SubscriptionSuccessPage })));
const SubscriptionManagementPage = React.lazy(() => import('./pages/subscription/SubscriptionManagementPage').then(m => ({ default: m.SubscriptionManagementPage })));

// Address pages
const AddressManagementPage = React.lazy(() => import('./pages/addresses/AddressManagementPage').then(m => ({ default: m.AddressManagementPage })));

// Menu pages
const TodaysMealPage = React.lazy(() => import('./pages/menu/TodaysMealPage').then(m => ({ default: m.TodaysMealPage })));

// Order pages
const OrdersListPage = React.lazy(() => import('./pages/orders/OrdersListPage').then(m => ({ default: m.OrdersListPage })));
const OrderDetailPage = React.lazy(() => import('./pages/orders/OrderDetailPage').then(m => ({ default: m.OrderDetailPage })));
const DeliveryTrackingPage = React.lazy(() => import('./pages/orders/DeliveryTrackingPage').then(m => ({ default: m.DeliveryTrackingPage })));

// Rating pages
const RateMealPage = React.lazy(() => import('./pages/ratings/RateMealPage').then(m => ({ default: m.RateMealPage })));

// Referral pages
const ReferralPage = React.lazy(() => import('./pages/referrals/ReferralPage').then(m => ({ default: m.ReferralPage })));

// Wallet pages
const WalletPage = React.lazy(() => import('./pages/wallet/WalletPage').then(m => ({ default: m.WalletPage })));
const ProgramsPage = React.lazy(() => import('./pages/programs/ProgramsPage').then(m => ({ default: m.ProgramsPage })));
const CoachingPage = React.lazy(() => import('./pages/coaching/CoachingPage').then(m => ({ default: m.CoachingPage })));
const TransformationChallengesPage = React.lazy(() => import('./pages/challenges/ChallengesPage').then(m => ({ default: m.ChallengesPage })));
const MetabolicPage = React.lazy(() => import('./pages/metabolic/MetabolicPage').then(m => ({ default: m.MetabolicPage })));
const FamilyPage = React.lazy(() => import('./pages/family/FamilyPage').then(m => ({ default: m.FamilyPage })));
const InsightsPage = React.lazy(() => import('./pages/insights/InsightsPage').then(m => ({ default: m.InsightsPage })));
const CorporatePage = React.lazy(() => import('./pages/corporate/CorporatePage').then(m => ({ default: m.CorporatePage })));
const SupplementsPage = React.lazy(() => import('./pages/supplements/SupplementsPage').then(m => ({ default: m.SupplementsPage })));
const ReadinessPage = React.lazy(() => import('./pages/workouts/ReadinessPage').then(m => ({ default: m.ReadinessPage })));
const HabitsPage = React.lazy(() => import('./pages/habits/HabitsPage').then(m => ({ default: m.HabitsPage })));
const IntegrationsPage = React.lazy(() => import('./pages/integrations/IntegrationsPage').then(m => ({ default: m.IntegrationsPage })));
const PrivacyPage = React.lazy(() => import('./pages/privacy/PrivacyPage').then(m => ({ default: m.PrivacyPage })));

// Notification pages
const NotificationsPage = React.lazy(() => import('./pages/notifications/NotificationsPage').then(m => ({ default: m.NotificationsPage })));

// Social pages
const FeedPage = React.lazy(() => import('./pages/social/FeedPage').then(m => ({ default: m.FeedPage })));
const CreatePostPage = React.lazy(() => import('./pages/social/CreatePostPage').then(m => ({ default: m.CreatePostPage })));
const UserProfilePage = React.lazy(() => import('./pages/social/UserProfilePage').then(m => ({ default: m.UserProfilePage })));

// Gamification pages
const AchievementsPage = React.lazy(() => import('./pages/gamification/AchievementsPage').then(m => ({ default: m.AchievementsPage })));
const ChallengesPage = React.lazy(() => import('./pages/gamification/ChallengesPage').then(m => ({ default: m.ChallengesPage })));
const LeaderboardPage = React.lazy(() => import('./pages/gamification/LeaderboardPage').then(m => ({ default: m.LeaderboardPage })));

// Reports & Scanner pages
const ReportsPage = React.lazy(() => import('./pages/reports/ReportsPage').then(m => ({ default: m.ReportsPage })));
const BarcodeScannerPage = React.lazy(() => import('./pages/scanner/BarcodeScannerPage').then(m => ({ default: m.BarcodeScannerPage })));

// Admin pages
const AdminLoginPage = React.lazy(() => import('./pages/admin/AdminLoginPage').then(m => ({ default: m.AdminLoginPage })));
const AdminLayout = React.lazy(() => import('./components/layout/AdminLayout').then(m => ({ default: m.AdminLayout })));
const AdminRoute = React.lazy(() => import('./components/guards/AdminRoute').then(m => ({ default: m.AdminRoute })));
const AdminDashboardPage = React.lazy(() => import('./pages/admin/AdminDashboardPage').then(m => ({ default: m.AdminDashboardPage })));
const UserManagementPage = React.lazy(() => import('./pages/admin/UserManagementPage').then(m => ({ default: m.UserManagementPage })));
const OrderManagementPage = React.lazy(() => import('./pages/admin/OrderManagementPage').then(m => ({ default: m.OrderManagementPage })));
const KitchenDashboardPage = React.lazy(() => import('./pages/admin/KitchenDashboardPage').then(m => ({ default: m.KitchenDashboardPage })));
const DeliveryDashboardPage = React.lazy(() => import('./pages/admin/DeliveryDashboardPage').then(m => ({ default: m.DeliveryDashboardPage })));
const AdminAnalyticsPage = React.lazy(() => import('./pages/admin/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));
const RecipeManagementPage = React.lazy(() => import('./pages/admin/RecipeManagementPage').then(m => ({ default: m.RecipeManagementPage })));
const MenuManagementPage = React.lazy(() => import('./pages/admin/MenuManagementPage').then(m => ({ default: m.MenuManagementPage })));
const AdminSubscriptionManagementPage = React.lazy(() => import('./pages/admin/SubscriptionManagementPage').then(m => ({ default: m.AdminSubscriptionManagementPage })));

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const location = useLocation();

  if (!isAuthenticated && !getToken()) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// /orders was renamed to /my-meals — preserve old bookmarks/shared links
// (including :id and :id/track) by redirecting to the equivalent new path.
const OrdersLegacyRedirect: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const suffix = location.pathname.endsWith('/track') ? '/track' : '';
  return <Navigate to={id ? `/my-meals/${id}${suffix}` : '/my-meals'} replace />;
};

// Onboarding Check Component
const OnboardingCheck: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { profile, isLoading } = useSelector((state: RootState) => state.userProfile);
  const location = useLocation();

  useEffect(() => {
    if (!profile && !isLoading) {
      dispatch(getProfile());
    }
  }, [dispatch, profile, isLoading]);

  if (isLoading) {
    return <PageLoader />;
  }

  if (location.pathname !== '/onboarding' &&
      (!profile ||
       profile.height_cm === null ||
       profile.height_cm === undefined ||
       profile.first_name === "" ||
       !profile.first_name)) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// Auth Route Component
const AuthRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  // Validate any stored token on startup before rendering routes.
  // This prevents stale/expired tokens from causing an AuthRoute → dashboard
  // → 401 → login redirect loop visible to the user.
  const [isValidating, setIsValidating] = useState(() => !!getToken());

  useEffect(() => {
    if (!getToken()) {
      setIsValidating(false);
      return;
    }
    dispatch(getCurrentUser())
      .unwrap()
      .catch(() => {
        // Token invalid or expired — getCurrentUser already called removeToken()
        dispatch(logout());
      })
      .finally(() => {
        setIsValidating(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch profile once authenticated (and after startup validation completes)
  useEffect(() => {
    if (!isValidating && isAuthenticated && getToken()) {
      dispatch(getProfile());
    }
  }, [dispatch, isAuthenticated, isValidating]);

  if (isValidating) {
    return <PageLoader />;
  }

  return (
    <Router>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public marketing site (landing, about, meal-plans, blog, legal) */}
          <Route element={<MarketingLayout />}>
            {marketingRoutes.map((r) => (
              <Route key={r.path} path={r.path} element={r.element} />
            ))}
            <Route path="/get-started" element={<GetStartedPage />} />
            <Route path="/meal-plans" element={<MealPlansPage />} />
          </Route>

          {/* Auth Routes */}
          <Route path="/login" element={<AuthRoute><LoginPage /></AuthRoute>} />
          <Route path="/signup" element={<AuthRoute><SignupPage /></AuthRoute>} />
          <Route path="/verify-otp" element={<AuthRoute><OTPPage /></AuthRoute>} />
          <Route path="/forgot-password" element={<AuthRoute><ForgotPasswordPage /></AuthRoute>} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Onboarding */}
          <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />

          {/* Protected App Routes */}
          <Route path="/dashboard" element={<ProtectedRoute><OnboardingCheck><DashboardPage /></OnboardingCheck></ProtectedRoute>} />

          {/* Meal Pages */}
          <Route path="/meals/photo" element={<ProtectedRoute><OnboardingCheck><MealPhotoCapturePage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/meals/add" element={<ProtectedRoute><OnboardingCheck><AddMealPage /></OnboardingCheck></ProtectedRoute>} />

          {/* Recipe Pages */}
          <Route path="/recipes" element={<ProtectedRoute><OnboardingCheck><RecipesPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/recipes/:id" element={<ProtectedRoute><OnboardingCheck><RecipeDetailPage /></OnboardingCheck></ProtectedRoute>} />

          {/* Food Search */}
          <Route path="/food-search" element={<ProtectedRoute><OnboardingCheck><FoodSearchPage /></OnboardingCheck></ProtectedRoute>} />

          {/* Health Pages */}
          <Route path="/health/weight" element={<ProtectedRoute><OnboardingCheck><WeightTrackingPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/health/water" element={<ProtectedRoute><OnboardingCheck><WaterTrackingPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/health/progress" element={<ProtectedRoute><OnboardingCheck><ProgressPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/health/insights" element={<ProtectedRoute><OnboardingCheck><HealthInsightsPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/health/progress-photos" element={<ProtectedRoute><OnboardingCheck><ProgressPhotosPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/health/body-composition" element={<ProtectedRoute><OnboardingCheck><BodyCompositionPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/health/meal-feedback" element={<ProtectedRoute><OnboardingCheck><MealFeedbackPage /></OnboardingCheck></ProtectedRoute>} />

          {/* Assistant */}
          <Route path="/assistant" element={<ProtectedRoute><OnboardingCheck><AssistantPage /></OnboardingCheck></ProtectedRoute>} />

          {/* Profile & Settings */}
          <Route path="/profile" element={<ProtectedRoute><OnboardingCheck><ProfilePage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/my-nutrition" element={<ProtectedRoute><OnboardingCheck><MyNutritionPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><OnboardingCheck><SettingsPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/contact" element={<ProtectedRoute><OnboardingCheck><ContactPage /></OnboardingCheck></ProtectedRoute>} />

          {/* Exercise Routes */}
          <Route path="/exercises" element={<ProtectedRoute><OnboardingCheck><ExercisesList /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/exercises/:id" element={<ProtectedRoute><OnboardingCheck><ExerciseDetail /></OnboardingCheck></ProtectedRoute>} />

          {/* Workout Routes */}
          <Route path="/workouts" element={<ProtectedRoute><OnboardingCheck><WorkoutPlansList /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/workouts/builder" element={<ProtectedRoute><OnboardingCheck><WorkoutBuilder /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/workouts/tracker" element={<ProtectedRoute><OnboardingCheck><WorkoutTracker /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/workouts/analytics" element={<ProtectedRoute><OnboardingCheck><WorkoutAnalytics /></OnboardingCheck></ProtectedRoute>} />

          {/* Diet Plan Routes */}
          <Route path="/diet-plans" element={<ProtectedRoute><OnboardingCheck><DietPlansList /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/diet-plans/generator" element={<ProtectedRoute><OnboardingCheck><MealPlanGenerator /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/diet-plans/shopping-list" element={<ProtectedRoute><OnboardingCheck><ShoppingList /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/diet-templates" element={<ProtectedRoute><OnboardingCheck><DietTemplatesPage /></OnboardingCheck></ProtectedRoute>} />

          {/* Subscription Routes */}
          <Route path="/subscribe" element={<ProtectedRoute><OnboardingCheck><SubscriptionTiersPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/subscribe/checkout" element={<ProtectedRoute><OnboardingCheck><CheckoutPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/subscribe/success" element={<ProtectedRoute><OnboardingCheck><SubscriptionSuccessPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/subscription" element={<ProtectedRoute><OnboardingCheck><SubscriptionManagementPage /></OnboardingCheck></ProtectedRoute>} />

          {/* Menu Routes */}
          <Route path="/menu" element={<Navigate to="/todays-meal" replace />} />
          <Route path="/todays-meal" element={<ProtectedRoute><OnboardingCheck><TodaysMealPage /></OnboardingCheck></ProtectedRoute>} />

          {/* Address Routes */}
          <Route path="/addresses" element={<ProtectedRoute><OnboardingCheck><AddressManagementPage /></OnboardingCheck></ProtectedRoute>} />

          {/* Order Routes (renamed to /my-meals; /orders kept as a redirect for old links) */}
          <Route path="/my-meals" element={<ProtectedRoute><OnboardingCheck><OrdersListPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/my-meals/:id" element={<ProtectedRoute><OnboardingCheck><OrderDetailPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/my-meals/:id/track" element={<ProtectedRoute><OnboardingCheck><DeliveryTrackingPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/orders" element={<OrdersLegacyRedirect />} />
          <Route path="/orders/:id" element={<OrdersLegacyRedirect />} />
          <Route path="/orders/:id/track" element={<OrdersLegacyRedirect />} />

          {/* Rating Routes */}
          <Route path="/rate/:orderItemId" element={<ProtectedRoute><OnboardingCheck><RateMealPage /></OnboardingCheck></ProtectedRoute>} />

          {/* Referral Routes */}
          <Route path="/referrals" element={<ProtectedRoute><OnboardingCheck><ReferralPage /></OnboardingCheck></ProtectedRoute>} />

          {/* Wallet Routes */}
          <Route path="/wallet" element={<ProtectedRoute><OnboardingCheck><WalletPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/programs" element={<ProtectedRoute><OnboardingCheck><ProgramsPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/coaching" element={<ProtectedRoute><OnboardingCheck><CoachingPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/challenges" element={<ProtectedRoute><OnboardingCheck><TransformationChallengesPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/metabolic" element={<ProtectedRoute><OnboardingCheck><MetabolicPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/family" element={<ProtectedRoute><OnboardingCheck><FamilyPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/insights" element={<ProtectedRoute><OnboardingCheck><InsightsPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/corporate" element={<ProtectedRoute><OnboardingCheck><CorporatePage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/supplements" element={<ProtectedRoute><OnboardingCheck><SupplementsPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/readiness" element={<ProtectedRoute><OnboardingCheck><ReadinessPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/habits" element={<ProtectedRoute><OnboardingCheck><HabitsPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/integrations" element={<ProtectedRoute><OnboardingCheck><IntegrationsPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/privacy-settings" element={<ProtectedRoute><OnboardingCheck><PrivacyPage /></OnboardingCheck></ProtectedRoute>} />

          {/* Notification Routes */}
          <Route path="/notifications" element={<ProtectedRoute><OnboardingCheck><NotificationsPage /></OnboardingCheck></ProtectedRoute>} />

          {/* Social Routes */}
          <Route path="/social" element={<ProtectedRoute><OnboardingCheck><FeedPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/social/create" element={<ProtectedRoute><OnboardingCheck><CreatePostPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/social/user/:userId" element={<ProtectedRoute><OnboardingCheck><UserProfilePage /></OnboardingCheck></ProtectedRoute>} />

          {/* Gamification Routes */}
          <Route path="/gamification/achievements" element={<ProtectedRoute><OnboardingCheck><AchievementsPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/gamification/challenges" element={<ProtectedRoute><OnboardingCheck><ChallengesPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/gamification/leaderboard" element={<ProtectedRoute><OnboardingCheck><LeaderboardPage /></OnboardingCheck></ProtectedRoute>} />

          {/* Reports & Scanner Routes */}
          <Route path="/reports" element={<ProtectedRoute><OnboardingCheck><ReportsPage /></OnboardingCheck></ProtectedRoute>} />
          <Route path="/scanner" element={<ProtectedRoute><OnboardingCheck><BarcodeScannerPage /></OnboardingCheck></ProtectedRoute>} />

          {/* Admin Login (public, no auth required) */}
          <Route
            path="/admin/login"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminLoginPage />
              </Suspense>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              </Suspense>
            }
          >
            <Route index element={<AdminDashboardPage />} />
            <Route path="users" element={<UserManagementPage />} />
            <Route path="orders" element={<OrderManagementPage />} />
            <Route path="kitchen" element={<KitchenDashboardPage />} />
            <Route path="delivery" element={<DeliveryDashboardPage />} />
            <Route path="analytics" element={<AdminAnalyticsPage />} />
            <Route path="recipes" element={<RecipeManagementPage />} />
            <Route path="menus" element={<MenuManagementPage />} />
            <Route path="subscriptions" element={<AdminSubscriptionManagementPage />} />
          </Route>

          {/* 404 */}
          <Route
            path="*"
            element={
              <div className="min-h-screen bg-cream-50 dark:bg-dark-surface flex items-center justify-center">
                <div className="text-center">
                  <h1 className="text-6xl font-bold text-gold-600 mb-4">404</h1>
                  <p className="text-xl text-neutral-700 dark:text-neutral-300 mb-4">Page not found</p>
                  <a href="/dashboard" className="text-gold-600 hover:text-neutral-9000">
                    Go to Dashboard
                  </a>
                </div>
              </div>
            }
          />
        </Routes>
      </Suspense>
      <OfflineIndicator />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            borderRadius: '12px',
            fontFamily: 'inherit',
            fontSize: '14px',
            fontWeight: '500',
            maxWidth: '380px',
          },
          success: {
            style: {
              background: '#065f46',
              color: '#d1fae5',
            },
            iconTheme: { primary: '#6ee7b7', secondary: '#065f46' },
          },
          error: {
            style: {
              background: '#7f1d1d',
              color: '#fee2e2',
            },
            iconTheme: { primary: '#fca5a5', secondary: '#7f1d1d' },
            duration: 5000,
          },
        }}
      />
    </Router>
  );
}

export default App;
